import React, { Fragment as F, useContext } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import { useTranslation } from 'react-i18next';
import Theme from '@dagensmat/carrot/Theme';
import { Container } from '@dagensmat/carrot/Layout';
import ProductCard from '_common/components/product-card/ProductCard';
import Button from '_common/components/button/Button.style';
import { ProductForSale } from 'types/Product';
import { Producer } from 'types/Producer';
import { track, PRODUCT_SEARCH_CLICK_SHOW_ALL_PRODUCTS } from 'utils/mixpanel';
import { showProductModal } from '_common/reducers/modal';
import { useAppDispatch } from '_common/hooks/reduxHooks';
import { DeliveryDateContext } from '_common/context/DeliveryDateContext';
import { GridContainer } from './grid/Grid';
import ProducerGroupHeader from './ProducerGroupHeader';

type GroupedSearchResultContainerProps = {
  producerGroups: {
    producer: Producer;
    numberOfOrders: number;
    products: ProductForSale[];
  }[];
  loadMore: () => void;
  hasMore: boolean;
};

const PRODUCTS_TO_SHOW = 6;

const GroupedSearchResultContainer = ({
  producerGroups,
  loadMore,
  hasMore
}: GroupedSearchResultContainerProps) => {
  const [showAllProductsForProducer, setShowAllProductsForProducer] =
    React.useState<string[]>([]);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { pageContext: deliveryDateContext } = useContext(DeliveryDateContext);

  return (
    <Container mt="xl">
      <InfiniteScroll hasMore={hasMore} loadMore={loadMore}>
        {producerGroups.map(({ producer, products }) => {
          const slicedProducts = products.slice(0, PRODUCTS_TO_SHOW);
          const onCardClick = (productId: string) => {
            dispatch(
              showProductModal({
                producerProducts: products,
                producer,
                productId,
                deliveryDateContext
              })
            );
          };

          return (
            <F key={producer._id}>
              <Container mb="jumbo">
                <ProducerGroupHeader producer={producer} />
                <GridContainer>
                  {(showAllProductsForProducer.includes(producer._id)
                    ? products
                    : slicedProducts
                  ).map(product => {
                    return (
                      <ProductCard
                        key={product._id}
                        product={product}
                        onClick={() => {
                          onCardClick(product._id);
                        }}
                      />
                    );
                  })}
                </GridContainer>
                {products.length > PRODUCTS_TO_SHOW &&
                  !showAllProductsForProducer.includes(producer._id) && (
                    <Button
                      style={{
                        whiteSpace: 'normal',
                        textAlign: 'left',
                        lineHeight: 1.2
                      }}
                      mt={Theme.Spacings.xs}
                      onClick={() => {
                        track(PRODUCT_SEARCH_CLICK_SHOW_ALL_PRODUCTS, {
                          producerId: producer._id
                        });
                        setShowAllProductsForProducer(prev => {
                          return [...prev, producer._id];
                        });
                      }}
                    >
                      {t('producer:ShowAllProductsFromProducer', {
                        producerName: producer.name
                      })}
                    </Button>
                  )}
              </Container>
            </F>
          );
        })}
      </InfiniteScroll>
    </Container>
  );
};

export default GroupedSearchResultContainer;
